import { all, call, getContext, put, takeLatest } from 'redux-saga/effects';

import type { ApiKey } from '../../_shared/domain/api-key.model';
import type { ApiKeysGateway } from '../infrastructure/api-keys.gateway';
import { apiKeysRequested, createKeyRequested, revokeKeyRequested } from './api-keys.actions';
import { apiKeysActions } from './api-keys.slice';

const LOAD_ERROR = 'Failed to load API keys.';
const CREATE_ERROR = 'Failed to create API key.';
const REVOKE_ERROR = 'Failed to revoke API key.';

/** Best-effort human message for the banner / snackbar (no domain error union). */
function toMessage(e: unknown, fallback: string): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * Refresh the rows without flipping the loading flag; used after a create /
 * revoke so the table reflects the change silently (mirrors the original page's
 * `fetchKeys()` refetch, which never re-showed the initial spinner).
 */
function* reloadRows(gateway: ApiKeysGateway) {
  const rows = (yield call(gateway.fetchApiKeys)) as ApiKey[];
  yield put(apiKeysActions.loadSucceeded(rows));
}

/** WORKER: fetch every API key. All I/O via the injected gateway. */
function* fetchWorker() {
  const gateway: ApiKeysGateway = yield getContext('apiKeysGateway');
  yield put(apiKeysActions.loadStarted());
  try {
    const rows = (yield call(gateway.fetchApiKeys)) as ApiKey[];
    yield put(apiKeysActions.loadSucceeded(rows));
  } catch (e) {
    yield put(apiKeysActions.loadFailed(toMessage(e, LOAD_ERROR)));
  }
}

/** WORKER: create a key, stash its one-time secret, then refresh the table. */
function* createWorker(action: ReturnType<typeof createKeyRequested>) {
  const { name } = action.payload;
  const gateway: ApiKeysGateway = yield getContext('apiKeysGateway');
  yield put(apiKeysActions.createStarted());
  try {
    const rawKey = (yield call(gateway.createApiKey, name)) as string;
    yield put(apiKeysActions.createSucceeded(rawKey));
    yield call(reloadRows, gateway);
  } catch (e) {
    yield put(apiKeysActions.createFailed(toMessage(e, CREATE_ERROR)));
  }
}

/** WORKER: revoke a key, then refresh the table. */
function* revokeWorker(action: ReturnType<typeof revokeKeyRequested>) {
  const id = action.payload;
  const gateway: ApiKeysGateway = yield getContext('apiKeysGateway');
  yield put(apiKeysActions.revokeStarted());
  try {
    yield call(gateway.revokeApiKey, id);
    yield put(apiKeysActions.revokeSucceeded());
    yield call(reloadRows, gateway);
  } catch (e) {
    yield put(apiKeysActions.revokeFailed(toMessage(e, REVOKE_ERROR)));
  }
}

/** WATCHERS. */
export function* apiKeysSaga() {
  yield all([
    takeLatest(apiKeysRequested.type, fetchWorker),
    takeLatest(createKeyRequested.type, createWorker),
    takeLatest(revokeKeyRequested.type, revokeWorker),
  ]);
}
